import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Column } from 'react-table'
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import NoVitalsTracked from '../../assets/images/ic-mdi-alert-octagon.2f7e6f0f.svg'
import AbnormalVitals from '../../assets/images/ic-attention.c8a8f118.svg'
import { ReactComponent as EditIcon } from 'assets/images/ic_edit.svg'
import { ReactComponent as ProfileIcon } from 'assets/images/ic-profile.svg'
import { useUrlQuery } from 'hooks/url'
import {
	capitalizeTwoCharacters,
	getClientDetails,
	useCheckScreenPermission,
	useSortHandler,
} from 'hooks/common'
import { APP_ROUTES } from 'const/router'
import { ENDPOINTS } from 'const/endpoints'
import { CONTACT_POINT, IAxiosResponseWithPagination } from 'types/entities'
import { IProvidersParams } from 'modules/Providers/Provider.interfaces'
import { convertUnix, formatDate, formatDateUTC } from 'utils/time'
import { COLUMN_NAME, SCREEN_NAME } from 'const/screen'
import { onGetTablebodyColumn, onGetTableColumns } from 'utils/common'
import ReactTooltip from 'react-tooltip'
import {
	IFullMember,
	IMemberParams,
	INonHealthCareReport,
	ProgramDropdownList,
} from './ProgramEnrollments.interfaces'
import { membersColumns } from './ProgramEnrollments.constants'
import { getMembers, getProgramsReenrollment, statusToggle } from './ProgramEnrollments.service'
import { DROPDOWN_LIMIT } from 'const/common'
import ToggleSwitch from 'components/Switch'
import {
	ENROLL_STATUS,
	ENROLL_TABLE_STATUS,
} from 'modules/Members/Member/MemberPrograms/MemberPrograms.constants'
import { IProgramParams } from 'modules/Programs/Program.interface'
import axiosLibrary from 'axios'

export function useMembersTableData({
	memberTableParams,
	tableSectionDetail,
	reEnroll,
}: any): [
	Column<{ [key: string]: React.ReactNode }>[],
	Column<{ [key: string]: React.ReactNode }>[],
	{ [key: string]: React.ReactNode }[],
	(param: string) => void,
	any,
] {
	const [params] = useUrlQuery<IProvidersParams>()
	const stringifyPartner = JSON.stringify(memberTableParams)

	const patients = useMembers(
		{
			...params,
			client: getClientDetails()?.clientId,
			program: params?.program,
			isEnabled: !!params?.isMultiEnrolled,
		},
		stringifyPartner && stringifyPartner,
	)

	const onSort = useSortHandler(params.sort)

	// To filter action particular column
	const dynamicColumn = tableSectionDetail?.providerColumns
	const membersTableColumn = onGetTableColumns(dynamicColumn)

	//  Custom hook to check is screen Available
	const isVitalScreenVisible = useCheckScreenPermission(SCREEN_NAME?.MEMBER_PROFILE_VITALS)
	const isProfileScreenVisible = useCheckScreenPermission(SCREEN_NAME?.MEMBER_PROFILE_PROFILE)

	// To filter action particular column if nothing to display in it
	const filteredMembersColumns = Array.isArray(membersTableColumn)
		? membersTableColumn?.filter((columnObj: any) => {
				return !(
					columnObj?.name?.toLowerCase() === COLUMN_NAME?.ACTION &&
					!isVitalScreenVisible &&
					!isProfileScreenVisible
				)
		  })
		: []

	const membersData = useMemo(() => {
		const mapBodyObj = (item: any) => {
			const phone = item?.patientContactPoints?.find(
				(item: any) => item?.contactPoint?.type === CONTACT_POINT.PHONE,
			)?.contactPoint?.text
			const email = item?.patientContactPoints?.find(
				(item: any) => item?.contactPoint?.type === CONTACT_POINT.EMAIL,
			)?.contactPoint?.text
			let contact = ''
			if (phone) {
				contact = phone
			} else {
				if (email) {
					contact = email
				}
			}
			const enrollmentPeriod = item?.patientEnrollmentPeriod
			const isStatusToggle =
				params?.enrollmentPeriodStatus === ENROLL_STATUS.ACTIVE && !enrollmentPeriod

			const status = item?.patientEnrollmentPeriod?.status || ENROLL_STATUS.NOT_ENROLLMENT
			return {
				name: (
					<div className="flex gap-2 ">
						{item?.vitalAlert && (
							<div className="">
								<div className="h-full border-r-2 border-red-500" />
							</div>
						)}
						<span>
							{isVitalScreenVisible ? (
								<Link
									to={`${APP_ROUTES.MEMBER_PROFILE}/${item?.id}/vitals`}
									className="text-fountainBlue"
								>
									{item?.humanName?.text?.charAt(0).toUpperCase() +
										item?.humanName?.text?.slice(1) || '--'}
								</Link>
							) : (
								<span>
									{item?.humanName?.text?.charAt(0).toUpperCase() +
										item?.humanName?.text?.slice(1) || '--'}
								</span>
							)}
							{item?.enrolledProgramCount > 0 && (
								<sup className="text-fountainBlue" data-tip="Number of programs enrolled">
									({item?.enrolledProgramCount})
								</sup>
							)}
						</span>
					</div>
				),

				Alert: (
					<div className="flex ">
						{item?.patientDevices?.some(
							(device: any) => device?.patientDeviceStatus === 'Abnormal',
						) ? (
							<span data-tip="With abnormal vitals result" className="flex gap-1">
								<img src={AbnormalVitals} alt="icon" />
							</span>
						) : (
							''
						)}
						{item?.patientDevices?.some((device: any) => device.vitalTwoDays == false) ? (
							<span data-tip="With no readings for 48h" className="flex gap-1">
								<img src={NoVitalsTracked} alt="icon" />
							</span>
						) : (
							''
						)}
					</div>
				),
				participantId: item?.patientProgram?.patientProgramId || '--',
				dateOfBirth: item?.dateOfBirth ? formatDateUTC(item?.dateOfBirth) : '--', //
				programEnrollmentDate: formatDate(+item?.enrolledDate) ?? '--',
				Email: (
					<a href={`mailto:${contact || '--'}`} className="text-fountainBlue">
						{contact || '--'}
					</a>
				),
				provider: item?.provider?.humanName?.text ?? '--',
				partner: item?.enrolledPartner?.companyName ?? item?.partner?.companyName ?? '--',
				status: item?.active ? 'Active' : 'Inactive',
				city: item?.address?.city?.charAt(0).toUpperCase() + item?.address?.city?.slice(1) || '--', //
				state: capitalizeTwoCharacters(item?.address?.state) || '--', //
				client: item?.client?.title || '--', //
				enrollmentStatus: (
					<div
						className={`${
							!isStatusToggle
								? 'pointer-events-none cursor-not-allowed'
								: 'text-fountainBlue cursor-pointer'
						} capitalize`}
						onClick={() => {
							reEnroll(item)
						}}
					>
						{getEnrollmentStatus(status)}
					</div>
				),
				// reEnroll: (
				// 	<ToggleSwitch
				// 		checked={isChecked}
				// 		disabled={isDisabled}
				// 		id={item?.id}
				// 		className={`${isDisabled || isChecked ? 'pointer-events-none cursor-not-allowed' : ''}`}
				// 		statusToggle={() => {
				// 			if (isStatusToggle) {
				// 				status(item).then(() => patients?.refetch())
				// 			}
				// 		}}
				// 		url={[ENDPOINTS.MEMBER_ENROLLMENT]}
				// 	/>
				// ),
				actions: (
					<div className="flex items-center gap-2">
						{isProfileScreenVisible && (
							<Link
								to={APP_ROUTES.MEMBER_PROFILE + `/${item?.id}/profile`}
								className="text-blue-500"
							>
								<span data-tip="Open profile">
									<EditIcon
										className="hover:stroke-fountainBlue  stroke-solidGray fill-solidGray hover:fill-fountainBlue"
										onClick={() => {
											ReactTooltip.hide()
										}}
									/>
								</span>
							</Link>
						)}
						{isVitalScreenVisible && (
							<Link
								to={`${APP_ROUTES.MEMBER_PROFILE}/${item?.id}/vitals`}
								className="text-blue-500"
							>
								<span data-tip="Open vitals">
									<ProfileIcon
										className="hover:stroke-fountainBlue  stroke-solidGray fill-solidGray hover:fill-fountainBlue"
										onClick={() => {
											ReactTooltip.hide()
										}}
									/>
								</span>
							</Link>
						)}
					</div>
				),
			}
		}
		return (
			patients?.data?.data?.items?.map((item: any) => {
				const bodyobj = mapBodyObj(item)
				return onGetTablebodyColumn(item, bodyobj, filteredMembersColumns)
			}, []) || []
		)
	}, [patients?.data?.data?.items, onSort, reEnroll, filteredMembersColumns])

	return [membersColumns, filteredMembersColumns, membersData, onSort, patients]
}

interface IPatientsQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<IFullMember>>>,
		Partial<IMemberParams> {}

export function useMembers(
	{
		id,
		page,
		limit,
		sort,
		name,
		client,
		status,
		program,
		city,
		state,
		isAlert,
		untrained,
		welcomeCall,
		patientReadings,
		currentDate,
		abnormalVitals,
		lastMeasurementLt,
		healthCondition,
		search,
		programEnrollmentPeriod,
		enrollmentStatus,
		...rest
	}: IPatientsQueryProps = {},
	partnerFilter?: any,
) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<IFullMember>>,
		any,
		IAxiosResponseWithPagination<Partial<IFullMember>>
	>(
		[
			ENDPOINTS.PATIENT_LISTING,
			page,
			limit,
			sort,
			name,
			client,
			status,
			program,
			welcomeCall,
			patientReadings,
			currentDate,
			abnormalVitals,
			city,
			state,
			isAlert,
			untrained,
			lastMeasurementLt,
			healthCondition,
			search,
			partnerFilter,
			id,
			programEnrollmentPeriod,
			enrollmentStatus,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getMembers(
				client,
				program,
				programEnrollmentPeriod,
				{
					page,
					limit,
					sort,
					name,
					status,
					welcomeCall,
					patientReadings,
					abnormalVitals,
					currentDate,
					city,
					state,
					isAlert,
					untrained,
					lastMeasurementLt,
					healthCondition,
					search,
					partner: partnerFilter,
					client,
					enrollmentStatus,
				},
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			suspense: false,
			enabled: !!programEnrollmentPeriod, // API called only if enrollmentPeriodID exists
			...rest,
		},
	)
}

interface ISmbpProgramQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<ProgramDropdownList>>>,
		Partial<INonHealthCareReport> {}

export function useProgramsReenrollment({
	limit = DROPDOWN_LIMIT.LIMIT,
	page = 1,
	search = '',
	sort = '',
	isEnabled = true,
	...rest
}: ISmbpProgramQueryProps) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<ProgramDropdownList>>,
		any,
		IAxiosResponseWithPagination<Partial<ProgramDropdownList>>
	>(
		[ENDPOINTS.PROGRAM_FILTER_LIST, limit, page, search, sort],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getProgramsReenrollment(
				{
					limit,
					page,
					search,
					sort,
				},
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

interface IUpdateStatusToggle {
	clientId?: string
	reEnrollmentData?: any
	id?: string
}
interface IUpdateStatusToggleProps
	extends Partial<UseMutationOptions<AxiosResponse<IProgramParams>, any, IUpdateStatusToggle>>,
		IUpdateStatusToggle {
	programEnrollmentPeriod?: string
	program?: string
	enrolledDate?: number
}

export function useStatusUpdateToggle({
	programEnrollmentPeriod,
	program,
	enrolledDate,
	...rest
}: IUpdateStatusToggleProps = {}) {
	const CancelToken = axiosLibrary.CancelToken
	const source = CancelToken.source()

	return useMutation<AxiosResponse<IProgramParams>, any, IUpdateStatusToggle>(
		async (variables: any) => {
			return await statusToggle(
				{
					patient: variables?.id,
					program,
					enrolledDate,
					programEnrollmentPeriod,
				},
				source,
			)
		},
		{
			...rest,
		},
	)
}

export const getEnrollmentStatus = (status: string) => {
	switch (status) {
		case ENROLL_STATUS.IN_PROGRESS:
			return ENROLL_TABLE_STATUS.IN_PROGRESS
		case ENROLL_STATUS.IN_REVIEW:
			return ENROLL_TABLE_STATUS.IN_REVIEW
		case ENROLL_STATUS.NOT_ENROLLMENT:
			return ENROLL_TABLE_STATUS.NOT_ENROLLMENT
		default:
			return status
	}
}
